import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
const SDK_URL = {
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
  production: "https://web.squarecdn.com/v1/square.js",
};

const TIERS = [
  ["fresh_care", "Fresh Care — $29/mo"],
  ["fresh_care_plus", "Fresh Care+ — $49/mo"],
  ["fresh_complete", "Fresh Complete — $79/mo"],
];

function loadSquareSdk(src) {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve();
    const existing = document.querySelector(`script[data-square="1"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Square SDK failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.dataset.square = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Square SDK failed to load"));
    document.head.appendChild(s);
  });
}

const STATUS_TONE = { ACTIVE: "#10B981", PENDING: "#F59E0B", CANCELED: "#EF4444", DEACTIVATED: "#EF4444" };

export function MembershipBilling({ customerId, jobId, onChange }) {
  const [cfg, setCfg] = useState(null);
  const [sub, setSub] = useState(undefined); // undefined = loading, null = none
  const [tier, setTier] = useState("fresh_care");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("view"); // view | updatecard
  const [updReady, setUpdReady] = useState(false);
  const cardRef = useRef(null);
  const cardUpdRef = useRef(null);
  const domId = `sq-mem-${customerId}`;
  const updDomId = `sq-memupd-${customerId}`;
  const createEndpoint = jobId ? `/jobs/${jobId}/membership/auto-bill` : `/customers/${customerId}/subscription`;

  const loadSub = async () => {
    try { const { data } = await api.get(`/customers/${customerId}/subscription`); setSub(data.subscription || null); }
    catch { setSub(null); }
  };
  useEffect(() => { loadSub(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (sub === undefined || sub) return; // only mount the card form when there is NO active subscription
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/square/config");
        if (cancelled) return;
        setCfg(data);
        if (!data.enabled) return;
        await loadSquareSdk(SDK_URL[data.environment] || SDK_URL.sandbox);
        if (cancelled || !window.Square) return;
        const payments = window.Square.payments(data.application_id, data.location_id);
        const card = await payments.card();
        await card.attach(`#${domId}`);
        if (cancelled) { try { card.destroy(); } catch { /* noop */ } return; }
        cardRef.current = card;
        setReady(true);
      } catch (e) { console.error("Square membership init failed", e); }
    })();
    return () => { cancelled = true; try { cardRef.current?.destroy(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub]);

  const start = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const res = await cardRef.current.tokenize();
      if (res.status !== "OK") throw new Error(res.errors?.[0]?.message || "Card was not accepted");
      const attempt = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `att-${Date.now()}`;
      const { data } = await api.post(createEndpoint, { tier, source_id: res.token, payment_attempt_id: attempt });
      toast.success(`Auto-billing started — ${data.status || "ACTIVE"}`);
      await loadSub(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Could not start auto-billing"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!sub || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/subscriptions/${sub.id}/cancel`, {});
      toast.success(data.benefit_end_date ? `Renewal stopped — benefits active until ${data.benefit_end_date}` : (data.status === "ACTIVE" ? "Membership canceled — active until period end" : `Membership canceled (${data.status})`));
      setSub(null); await loadSub(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
    finally { setBusy(false); }
  };

  const resume = async () => {
    if (!sub || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/subscriptions/${sub.id}/resume`, {});
      if (data.resumed) toast.success(`Membership resumed — renewal restored (${data.status})`);
      else toast.message(data.detail || "Square could not reinstate this cancellation.", { duration: 8000 });
      await loadSub(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Resume failed"); }
    finally { setBusy(false); }
  };

  // Mount a fresh Square card element for the "update card" flow.
  useEffect(() => {
    if (mode !== "updatecard") { setUpdReady(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/square/config");
        if (cancelled || !data.enabled) return;
        await loadSquareSdk(SDK_URL[data.environment] || SDK_URL.sandbox);
        if (cancelled || !window.Square) return;
        const payments = window.Square.payments(data.application_id, data.location_id);
        const card = await payments.card();
        await card.attach(`#${updDomId}`);
        if (cancelled) { try { card.destroy(); } catch { /* noop */ } return; }
        cardUpdRef.current = card; setUpdReady(true);
      } catch (e) { console.error("update-card init failed", e); }
    })();
    return () => { cancelled = true; try { cardUpdRef.current?.destroy(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const submitUpdateCard = async () => {
    if (!cardUpdRef.current || busy) return;
    setBusy(true);
    try {
      const res = await cardUpdRef.current.tokenize();
      if (res.status !== "OK") throw new Error(res.errors?.[0]?.message || "Card was not accepted");
      const attempt = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `att-${Date.now()}`;
      const { data } = await api.post(`/subscriptions/${sub.id}/update-card`, { source_id: res.token, payment_attempt_id: attempt });
      toast.success(`Card updated — ${data.card_brand || ""} ••••${data.card_last4 || ""}. Square will retry the charge.`);
      setMode("view"); await loadSub(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Card update failed"); }
    finally { setBusy(false); }
  };

  if (sub === undefined) return <div className="text-sm" style={{ color: "var(--muted)" }} data-testid="membership-billing-loading">Loading membership…</div>;

  if (cfg && !cfg.enabled) {
    return <div className="text-sm" style={{ color: "var(--warn)" }} data-testid="membership-billing-disabled">Square is not configured — auto-billing unavailable.</div>;
  }

  if (sub) {
    const tone = STATUS_TONE[sub.status] || "var(--txt2)";
    const failed = sub.last_payment_status === "FAILED";
    return (
      <div data-testid="membership-billing-active">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 size={16} style={{ color: tone }} />
          <span className="font-head font-bold" data-testid="membership-tier">{TIERS.find((t) => t[0] === sub.tier)?.[1] || sub.tier}</span>
          <span className="chip" style={{ fontSize: 10, color: tone, borderColor: tone }} data-testid="membership-status">{sub.status}</span>
          {sub.environment === "sandbox" && <span className="chip" style={{ fontSize: 10 }}>SANDBOX</span>}
        </div>
        <div className="text-xs" style={{ color: "var(--txt2)" }}>
          ${sub.monthly}/month · recurring auto-billing{sub.card_brand ? ` · ${sub.card_brand} ••••${sub.card_last4 || ""}` : ""}
        </div>
        {sub.next_billing_date && !sub.cancel_scheduled && (
          <div className="text-xs mt-1" style={{ color: "var(--txt2)" }} data-testid="membership-next-billing">Next billing / paid through: <b style={{ color: "var(--txt)" }}>{sub.next_billing_date}</b></div>
        )}
        {sub.cancel_scheduled && (
          <div className="text-xs mt-2 p-2 rounded-lg" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }} data-testid="membership-benefit-end">
            Renewal stopped — benefits remain active until <b>{sub.benefit_end_date || "period end"}</b>, then expire automatically.
          </div>
        )}
        {failed && (
          <div className="text-xs mt-2 p-2 rounded-lg flex items-center gap-1" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }} data-testid="membership-payment-failed">
            <AlertTriangle size={13} /> Last automatic payment FAILED — update the member's card.
          </div>
        )}
        {mode === "updatecard" ? (
          <div className="mt-3" data-testid="membership-updatecard">
            <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--txt2)" }}>New card on file (Square)</div>
            <div id={updDomId} data-testid="membership-updatecard-container" style={{ minHeight: 46 }} />
            <div className="flex gap-2 mt-2">
              <button className="sg-btn sg-btn-accent flex-1" style={{ minHeight: 40 }} disabled={!updReady || busy} onClick={submitUpdateCard} data-testid="membership-updatecard-submit">{busy ? "Saving…" : "Save New Card"}</button>
              <button className="sg-btn" style={{ minHeight: 40 }} disabled={busy} onClick={() => setMode("view")} data-testid="membership-updatecard-cancel">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mt-3">
            <button className="sg-btn" style={{ minHeight: 36 }} disabled={busy} onClick={loadSub} data-testid="membership-refresh"><RefreshCw size={14} /> Refresh</button>
            {failed && <button className="sg-btn sg-btn-accent" style={{ minHeight: 36 }} disabled={busy} onClick={() => setMode("updatecard")} data-testid="membership-updatecard-btn"><CreditCard size={14} /> Update Card</button>}
            {sub.cancel_scheduled && !sub.benefit_expired && (
              <button className="sg-btn" style={{ minHeight: 36, color: "#10B981", borderColor: "#10B981" }} disabled={busy} onClick={resume} data-testid="membership-resume-btn">{busy ? "…" : "Resume Membership"}</button>
            )}
            {!sub.cancel_scheduled && (
              <button className="sg-btn" style={{ minHeight: 36, color: "#EF4444", borderColor: "#EF4444" }} disabled={busy} onClick={cancel} data-testid="membership-cancel">{busy ? "…" : "Cancel Membership"}</button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="membership-billing-setup">
      <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--txt2)" }}>Start recurring auto-billing</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {TIERS.map(([k, l]) => (
          <button key={k} className="sg-btn" style={{ minHeight: 36,
            background: tier === k ? "var(--accent)" : undefined, color: tier === k ? "#fff" : undefined, borderColor: tier === k ? "var(--accent)" : undefined }}
            onClick={() => setTier(k)} data-testid={`membership-tier-${k}`}>{tier === k ? "✓ " : ""}{l}</button>
        ))}
      </div>
      <div id={domId} data-testid="membership-card-container" style={{ minHeight: 46 }} />
      <button className="sg-btn sg-btn-accent w-full mt-3" style={{ minHeight: 44 }} disabled={!ready || busy} onClick={start} data-testid="membership-start-btn">
        <CreditCard size={16} /> {busy ? "Processing…" : "Start Auto-Billing"}
      </button>
      <div className="text-[11px] mt-2" style={{ color: "var(--muted)" }}>
        Card is stored securely on file with Square (Sandbox). Recurring monthly charges are handled automatically by Square.
      </div>
    </div>
  );
}
